import {
  analyticsApi,
  type AnalyticsRange,
  type ProjectGoal,
} from '@shared/api-client';
import { useQueries } from '@tanstack/react-query';
import { Flag, Settings2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { HelpTooltip } from '@/components/ui/help-tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCompactNumber } from '@/lib/utils';

const formatRate = (value: number | null | undefined): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  return `${value < 10 ? value.toFixed(1) : value.toFixed(0)}%`;
};

export const GoalsReport = ({
  projectId,
  range,
  goals,
  goalsLoading,
  goalsError,
  selectedGoalId,
  onGoalChange,
}: {
  projectId: string;
  range: AnalyticsRange;
  goals: ProjectGoal[];
  goalsLoading: boolean;
  goalsError: boolean;
  selectedGoalId?: string;
  onGoalChange: (goalId: string) => void;
}) => {
  const analytics = useQueries({
    queries: goals.map((goal) => ({
      queryKey: ['analytics', 'project', projectId, 'goal', goal.id, range],
      queryFn: () => analyticsApi.getGoalAnalytics(projectId, goal.id, range),
      retry: 1,
      staleTime: 30_000,
    })),
  });
  const rows = goals.map((goal, index) => ({
    goal,
    summary: analytics[index]?.data?.summary,
    loading: analytics[index]?.isLoading ?? false,
    error: analytics[index]?.isError ?? false,
  }));
  const maximum = Math.max(
    ...rows.map((row) => row.summary?.conversions ?? 0),
    1,
  );

  return (
    <Card className="overflow-hidden shadow-none">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-3.5 sm:px-6">
        <div className="flex items-center gap-1.5">
          <h2 className="text-sm font-semibold text-ink">Goals</h2>
          <HelpTooltip label="About goals">
            Unique counts each visitor once. Total counts every completion. CR
            is unique conversions divided by visitors in the selected period.
            Select a goal to show it in the growth chart.
          </HelpTooltip>
        </div>
        <Link
          to={`/projects/${encodeURIComponent(projectId)}/details`}
          className="inline-flex items-center gap-1 text-xs font-medium text-ink-muted transition-colors hover:text-ink"
        >
          Manage
          <Settings2 aria-hidden="true" className="size-3.5" />
        </Link>
      </div>

      {goalsLoading ? (
        <div className="space-y-2 p-4 sm:px-6">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </div>
      ) : goalsError ? (
        <div className="flex min-h-24 items-center justify-center px-6 text-sm text-ink-muted">
          Goals could not be loaded.
        </div>
      ) : rows.length === 0 ? (
        <div className="flex min-h-24 items-center justify-between gap-4 px-5 py-5 sm:px-6">
          <div className="flex min-w-0 items-center gap-3 text-sm text-ink-muted">
            <Flag aria-hidden="true" className="size-4 shrink-0" />
            <span>Choose an event or page visit to measure conversion.</span>
          </div>
          <Link
            to={`/projects/${encodeURIComponent(projectId)}/details`}
            className="shrink-0 text-sm font-medium text-accent hover:text-accent-hover"
          >
            Add goal
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[36rem]">
            <div className="grid grid-cols-[minmax(14rem,1fr)_6rem_6rem_5rem] gap-3 px-5 py-2 text-xs text-ink-subtle sm:px-6">
              <span>Goal</span>
              <span className="text-right">Unique</span>
              <span className="text-right">Total</span>
              <span className="text-right">CR</span>
            </div>
            <div className="divide-y divide-border border-t border-border">
              {rows.map(({ goal, summary, loading, error }) => {
                const selected = goal.id === selectedGoalId;
                const width =
                  summary && summary.conversions > 0
                    ? Math.max((summary.conversions / maximum) * 100, 1.5)
                    : 0;
                return (
                  <button
                    key={goal.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => onGoalChange(goal.id)}
                    className={cn(
                      'group relative grid min-h-12 w-full grid-cols-[minmax(14rem,1fr)_6rem_6rem_5rem] items-center gap-3 overflow-hidden px-5 text-left transition-colors sm:px-6',
                      selected
                        ? 'bg-accent-subtle'
                        : 'hover:bg-surface-muted/70',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className={cn(
                        'absolute inset-y-1.5 left-0 transition-[width] duration-200 motion-reduce:transition-none',
                        selected ? 'bg-accent/10' : 'bg-surface-muted',
                      )}
                      style={{ width: `${width}%` }}
                    />
                    <span className="relative flex min-w-0 items-center gap-2">
                      <span className="truncate text-sm font-medium text-ink">
                        {goal.name}
                      </span>
                      {goal.isPrimary ? (
                        <span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-[11px] font-medium text-ink-muted ring-1 ring-border">
                          Primary
                        </span>
                      ) : null}
                    </span>
                    {loading ? (
                      <Skeleton className="relative ml-auto h-4 w-10" />
                    ) : (
                      <span className="relative text-right text-sm tabular-nums text-ink">
                        {error
                          ? '—'
                          : formatCompactNumber(summary?.conversions ?? 0)}
                      </span>
                    )}
                    {loading ? (
                      <Skeleton className="relative ml-auto h-4 w-10" />
                    ) : (
                      <span className="relative text-right text-sm tabular-nums text-ink-muted">
                        {error
                          ? '—'
                          : formatCompactNumber(summary?.totalCompletions ?? 0)}
                      </span>
                    )}
                    {loading ? (
                      <Skeleton className="relative ml-auto h-4 w-10" />
                    ) : (
                      <span className="relative text-right text-sm tabular-nums text-ink-muted">
                        {error ? '—' : formatRate(summary?.conversionRate)}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
